import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';

import {
  NotificationAck,
  NotificationConnectionStatus,
  NotificationRecord,
  NotificationSource,
  NotificationsRealtimePayload,
  NotificationsState,
} from '../types/Notifications/types';
import {
  buildNotificationFromPush,
  buildNotificationRecord,
  countUnreadNotifications,
  enqueueNotificationAck,
  updateNotificationTimestamp,
  upsertNotification,
} from '../utils/notificationHelpers';
import {
  NOTIFICATION_TRACKING_EVENTS,
  buildNotificationSeenTrackingPayload,
  notificationTrackingEvents,
} from '../utils/notificationTrackingEvents';
import {
  PersistedNotificationsState,
  clearNotificationsState,
  loadNotificationsState,
  saveNotificationsState,
} from '../utils/notificationsStorage';
import { useUser } from './UserContext';

type NotificationsAction =
  | { type: 'hydrate'; payload: PersistedNotificationsState | null }
  | { type: 'ingest'; payload: NotificationRecord }
  | {
      type: 'ingestMany';
      payload: { items: NotificationRecord[]; cursor?: string | null };
    }
  | { type: 'markOpened'; payload: { id: string; source: NotificationSource } }
  | { type: 'markSeen'; payload: { id: string; source: NotificationSource } }
  | { type: 'markAllSeen' }
  | { type: 'removeAck'; payload: NotificationAck }
  | { type: 'setConnectionStatus'; payload: NotificationConnectionStatus }
  | { type: 'setRealtimeEnabled'; payload: boolean }
  | { type: 'setLastError'; payload: string | null }
  | { type: 'reset' };

const initialState: NotificationsState = {
  items: [],
  unreadCount: 0,
  connectionStatus: 'disconnected',
  lastSyncAt: null,
  lastCursor: null,
  pendingAcks: [],
  realtimeEnabled: false,
  lastError: null,
};

const recalculateState = (
  state: NotificationsState,
  items: NotificationRecord[],
  pendingAcks: NotificationAck[] = state.pendingAcks,
): NotificationsState => ({
  ...state,
  items,
  unreadCount: countUnreadNotifications(items),
  pendingAcks,
});

const notificationsReducer = (
  state: NotificationsState,
  action: NotificationsAction,
): NotificationsState => {
  switch (action.type) {
    case 'hydrate': {
      if (!action.payload) return initialState;
      const items = action.payload.items ?? [];
      return {
        ...initialState,
        items,
        unreadCount: countUnreadNotifications(items),
        pendingAcks: action.payload.pendingAcks ?? [],
        lastSyncAt: action.payload.lastSyncAt ?? null,
        lastCursor: action.payload.lastCursor ?? null,
      };
    }
    case 'ingest': {
      const alreadyExists = state.items.some((item) => item.id === action.payload.id);
      const items = upsertNotification(state.items, action.payload);
      const pendingAcks = alreadyExists
        ? state.pendingAcks
        : enqueueNotificationAck(
            state.pendingAcks,
            action.payload.id,
            'received',
            action.payload.source,
          );
      return recalculateState(state, items, pendingAcks);
    }
    case 'ingestMany': {
      let nextItems = state.items;
      let nextAcks = state.pendingAcks;

      action.payload.items.forEach((item) => {
        const alreadyExists = nextItems.some((existing) => existing.id === item.id);
        nextItems = upsertNotification(nextItems, item);
        if (!alreadyExists) {
          nextAcks = enqueueNotificationAck(nextAcks, item.id, 'received', item.source);
        }
      });

      const cursor = action.payload.cursor ?? nextItems.at(0)?.id ?? state.lastCursor;

      return {
        ...recalculateState(state, nextItems, nextAcks),
        lastSyncAt: new Date().toISOString(),
        lastCursor: cursor,
      };
    }
    case 'markOpened': {
      const items = updateNotificationTimestamp(
        state.items,
        action.payload.id,
        'openedAt',
      );
      const pendingAcks = enqueueNotificationAck(
        state.pendingAcks,
        action.payload.id,
        'opened',
        action.payload.source,
      );
      return recalculateState(state, items, pendingAcks);
    }
    case 'markSeen': {
      const items = updateNotificationTimestamp(state.items, action.payload.id, 'seenAt');
      const pendingAcks = enqueueNotificationAck(
        state.pendingAcks,
        action.payload.id,
        'seen',
        action.payload.source,
      );
      return recalculateState(state, items, pendingAcks);
    }
    case 'markAllSeen': {
      const seenAt = new Date().toISOString();
      const items = state.items.map((item) =>
        item.seenAt
          ? item
          : {
              ...item,
              seenAt,
            },
      );

      const pendingAcks = state.items.reduce<NotificationAck[]>((queue, item) => {
        if (item.seenAt) return queue;
        return enqueueNotificationAck(queue, item.id, 'seen', item.source);
      }, state.pendingAcks);

      return recalculateState(state, items, pendingAcks);
    }
    case 'removeAck': {
      const pendingAcks = state.pendingAcks.filter(
        (ack) => !(ack.id === action.payload.id && ack.type === action.payload.type),
      );
      return { ...state, pendingAcks };
    }
    case 'setConnectionStatus':
      return {
        ...state,
        connectionStatus: action.payload,
      };
    case 'setRealtimeEnabled':
      return {
        ...state,
        realtimeEnabled: action.payload,
      };
    case 'setLastError':
      return {
        ...state,
        lastError: action.payload,
      };
    case 'reset':
      return initialState;
    default:
      return state;
  }
};

type NotificationsContextType = {
  state: NotificationsState;
  ingestRealtimeNotification: (
    payload: NotificationsRealtimePayload,
    source?: Exclude<NotificationSource, 'fcm'>,
  ) => NotificationRecord;
  ingestPushNotification: (
    notification: Parameters<typeof buildNotificationFromPush>[0],
  ) => NotificationRecord;
  ingestSyncedNotifications: (
    payloads: NotificationsRealtimePayload[],
    cursor?: string | null,
  ) => NotificationRecord[];
  markNotificationOpened: (id: string, source?: NotificationSource) => void;
  markNotificationSeen: (id: string, source?: NotificationSource) => void;
  markAllNotificationsSeen: () => void;
  flushPendingAcks: (sender: (ack: NotificationAck) => Promise<void>) => Promise<void>;
  setConnectionStatus: (status: NotificationConnectionStatus) => void;
  setRealtimeEnabled: (enabled: boolean) => void;
  setLastError: (error: string | null) => void;
  clearNotifications: () => Promise<void>;
};

const NotificationsContext = createContext<NotificationsContextType | null>(null);

interface NotificationsProviderProps {
  children: ReactNode;
}

export const NotificationsProvider: React.FC<NotificationsProviderProps> = ({
  children,
}) => {
  const { user } = useUser();
  const [state, dispatch] = useReducer(notificationsReducer, initialState);
  const stateRef = useRef(state);
  const hasHydratedRef = useRef(false);
  const userEmail = user?.email?.trim().toLowerCase() ?? '';
  const storageScope = userEmail || 'anonymous';

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    let cancelled = false;

    const hydrate = async () => {
      const persistedState = await loadNotificationsState(storageScope);
      if (cancelled) return;
      dispatch({ type: 'hydrate', payload: persistedState });
      hasHydratedRef.current = true;
    };

    hydrate();

    return () => {
      cancelled = true;
    };
  }, [storageScope]);

  useEffect(() => {
    if (!hasHydratedRef.current) return;

    void saveNotificationsState(storageScope, {
      items: state.items,
      pendingAcks: state.pendingAcks,
      lastSyncAt: state.lastSyncAt,
      lastCursor: state.lastCursor,
    });
  }, [state.items, state.pendingAcks, state.lastSyncAt, state.lastCursor, storageScope]);

  const ingestRealtimeNotification = useCallback(
    (
      payload: NotificationsRealtimePayload,
      source: Exclude<NotificationSource, 'fcm'> = 'websocket',
    ): NotificationRecord => {
      const notification = buildNotificationRecord(payload, source);
      dispatch({ type: 'ingest', payload: notification });
      return notification;
    },
    [],
  );

  const ingestPushNotification = useCallback(
    (
      notification: Parameters<typeof buildNotificationFromPush>[0],
    ): NotificationRecord => {
      const normalizedNotification = buildNotificationFromPush(notification);
      dispatch({ type: 'ingest', payload: normalizedNotification });
      return normalizedNotification;
    },
    [],
  );

  const ingestSyncedNotifications = useCallback(
    (payloads: NotificationsRealtimePayload[], cursor?: string | null) => {
      const seenIds = new Set(stateRef.current.items.map((item) => item.id));
      const items = payloads.map((payload) => buildNotificationRecord(payload, 'sync'));
      const newItems = items.filter((item) => {
        if (seenIds.has(item.id)) return false;
        seenIds.add(item.id);
        return true;
      });

      dispatch({ type: 'ingestMany', payload: { items, cursor } });
      return newItems;
    },
    [],
  );

  const markNotificationOpened = useCallback(
    (id: string, source: NotificationSource = 'websocket') => {
      dispatch({ type: 'markOpened', payload: { id, source } });
    },
    [],
  );

  const markNotificationSeen = useCallback(
    (id: string, source: NotificationSource = 'websocket') => {
      const currentNotification = stateRef.current.items.find((item) => item.id === id);

      dispatch({ type: 'markSeen', payload: { id, source } });

      if (!currentNotification || currentNotification.seenAt) return;

      notificationTrackingEvents.emit(
        NOTIFICATION_TRACKING_EVENTS.SEEN,
        buildNotificationSeenTrackingPayload(
          [
            {
              id: currentNotification.id,
              type: currentNotification.type,
              source: currentNotification.source,
            },
          ],
          'single',
        ),
      );
    },
    [],
  );

  const markAllNotificationsSeen = useCallback(() => {
    const unseenNotifications = stateRef.current.items.filter((item) => !item.seenAt);

    dispatch({ type: 'markAllSeen' });

    if (!unseenNotifications.length) return;

    notificationTrackingEvents.emit(
      NOTIFICATION_TRACKING_EVENTS.SEEN,
      buildNotificationSeenTrackingPayload(
        unseenNotifications.map((item) => ({
          id: item.id,
          type: item.type,
          source: item.source,
        })),
        'bulk',
      ),
    );
  }, []);

  const flushPendingAcks = useCallback(
    async (sender: (ack: NotificationAck) => Promise<void>) => {
      const queue = [...stateRef.current.pendingAcks];

      for (const ack of queue) {
        try {
          await sender(ack);
          dispatch({ type: 'removeAck', payload: ack });
        } catch (error) {
          console.error('[Notifications] Error enviando ACK', error);
          break;
        }
      }
    },
    [],
  );

  const setConnectionStatus = useCallback((status: NotificationConnectionStatus) => {
    dispatch({ type: 'setConnectionStatus', payload: status });
  }, []);

  const setRealtimeEnabled = useCallback((enabled: boolean) => {
    dispatch({ type: 'setRealtimeEnabled', payload: enabled });
  }, []);

  const setLastError = useCallback((error: string | null) => {
    dispatch({ type: 'setLastError', payload: error });
  }, []);

  const clearNotifications = useCallback(async () => {
    dispatch({ type: 'reset' });
    await clearNotificationsState(storageScope);
  }, [storageScope]);

  const value = useMemo<NotificationsContextType>(
    () => ({
      state,
      ingestRealtimeNotification,
      ingestPushNotification,
      ingestSyncedNotifications,
      markNotificationOpened,
      markNotificationSeen,
      markAllNotificationsSeen,
      flushPendingAcks,
      setConnectionStatus,
      setRealtimeEnabled,
      setLastError,
      clearNotifications,
    }),
    [
      state,
      ingestRealtimeNotification,
      ingestPushNotification,
      ingestSyncedNotifications,
      markNotificationOpened,
      markNotificationSeen,
      markAllNotificationsSeen,
      flushPendingAcks,
      setConnectionStatus,
      setRealtimeEnabled,
      setLastError,
      clearNotifications,
    ],
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotificationsContext = (): NotificationsContextType => {
  const context = useContext(NotificationsContext);

  if (!context) {
    throw new Error(
      'useNotificationsContext must be used within a NotificationsProvider',
    );
  }

  return context;
};
